import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { MapPin } from 'lucide-react';

function DistrictAnalysisChart({ districts }) {
  if (!districts || districts.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 font-montserrat">No hay datos por distrito</p>
      </div>
    );
  }

  // Sort by total deliveries and take top 10
  const topDistricts = [...districts]
    .sort((a, b) => b.total_deliveries - a.total_deliveries)
    .slice(0, 10);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="mb-6">
        <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-2">
          Top 10 Distritos por Volumen
        </h3>
        <p className="text-sm text-slate-600 font-montserrat">
          Comparación de entregas totales y entregadas por distrito
        </p>
      </div>

      {/* Chart */}
      <div className="h-96 mb-6">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={topDistricts}
            margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis 
              dataKey="district" 
              angle={-45}
              textAnchor="end"
              height={100}
              tick={{ fill: '#64748b', fontSize: 12 }}
            />
            <YAxis tick={{ fill: '#64748b', fontSize: 12 }} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontFamily: 'Montserrat'
              }}
            />
            <Legend 
              wrapperStyle={{ 
                fontFamily: 'Montserrat',
                fontSize: '14px'
              }}
            />
            <Bar 
              dataKey="total_deliveries" 
              fill="#B1336E" 
              name="Total Entregas"
              radius={[8, 8, 0, 0]}
            />
            <Bar 
              dataKey="delivered" 
              fill="#67A43C" 
              name="Entregados"
              radius={[8, 8, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* District Stats Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 font-montserrat">
                Distrito
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 font-montserrat">
                Total
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 font-montserrat">
                Entregados
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 font-montserrat">
                Tasa Éxito
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 font-montserrat">
                Tiempo Prom.
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {topDistricts.map((district, index) => (
              <tr key={index} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3">
                  <p className="text-sm font-semibold text-slate-900 font-montserrat">
                    {district.district}
                  </p>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-sm text-slate-900 font-montserrat">
                    {district.total_deliveries}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-sm text-[#67A43C] font-semibold font-montserrat">
                    {district.delivered}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold font-montserrat ${
                    district.success_rate >= 90 
                      ? 'bg-[#67A43C]/10 text-[#67A43C]'
                      : district.success_rate >= 70
                      ? 'bg-[#E6B43C]/10 text-[#E6B43C]'
                      : 'bg-red-50 text-red-600'
                  }`}>
                    {district.success_rate}%
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-sm text-slate-600 font-montserrat">
                    {district.avg_delivery_hours.toFixed(1)} hrs
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default DistrictAnalysisChart;